import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Compass } from "lucide-react";
import { cn } from "@/lib/utils";

export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  useTransform(scrollY, [0, 100], [0, 1]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        "fixed inset-x-0 top-0 z-40 transition-all duration-500",
        scrolled
          ? "border-b border-rule/60 bg-obsidian/85 backdrop-blur-xl"
          : "border-b border-transparent"
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:px-10">
        <Link
          to="/"
          data-testid="nav-brand"
          className="flex items-center gap-2.5 group"
        >
          <span className="relative flex h-6 w-6 items-center justify-center border border-teal/70">
            <Compass className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            <span className="absolute -top-px -left-px h-1 w-1 bg-amber" />
          </span>
          <span className="font-display text-lg tracking-tight text-ink">
            Life OS
          </span>
          <span className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-mist md:inline">
            &nbsp;/ v0.1
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {[
            ["Modules", "/#modules"],
            ["Signals", "/#signals"],
            ["Manifesto", "/manifesto"],
            ["About", "/about"],
          ].map(([label, href]) => (
            <Link
              key={label}
              to={href}
              data-testid={`nav-link-${label.toLowerCase()}`}
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:text-ink"
            >
              {label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="nav-preview-btn"
            className="hidden items-center gap-2 border border-rule px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:border-amber sm:flex"
          >
            Sign in
            <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
          </Link>
          <a
            href="#waitlist"
            data-testid="nav-waitlist-btn"
            className="flex items-center gap-2 bg-teal px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-teal-hover"
          >
            Join waitlist
          </a>
        </div>
      </div>
    </motion.header>
  );
}
